import React, { useState, useEffect } from "react";
import { StyleSheet, View, ScrollView } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { FilterChip } from "./FilterChip";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useFirebase } from "@components/firebase/useFirebase";
import { ITag, ITagFilterCriteria } from "common247";
import { getTags } from "@/services/firebase/tag-functions";

export interface ITagFilterComponentProps {
  criteria: ITagFilterCriteria;
  onChange: (updated: ITagFilterCriteria) => void;
  availableTags?: ITag[];
}

/**
 * Filter controls specific to Tags across the platform.
 */
export function TagFilter({
  criteria,
  onChange,
  availableTags,
}: ITagFilterComponentProps): React.JSX.Element {
  const services = useFirebase();
  const [internalTags, setInternalTags] = useState<ITag[]>(availableTags || []);
  const [searchQuery, setSearchQuery] = useState("");
  const textMuted = useThemeColor({}, "textMuted");

  const selectedTagIds = criteria.tagIds || [];

  useEffect(() => {
    if (availableTags && availableTags.length > 0) {
      setInternalTags(availableTags);
      return;
    }
    if (!services) return;
    let isMounted = true;
    getTags(services, {})
      .then((res) => {
        const list = res.tags || res.data?.tags;
        if (isMounted && res.status && list) {
          setInternalTags(list);
        }
      })
      .catch((err) => console.warn("[TagFilter] Failed to fetch tags:", err));

    return () => {
      isMounted = false;
    };
  }, [services, availableTags]);

  const toggleTag = (id: string): void => {
    const updated = selectedTagIds.includes(id)
      ? selectedTagIds.filter((tId) => tId !== id)
      : [...selectedTagIds, id];
    onChange({ ...criteria, tagIds: updated.length > 0 ? updated : undefined });
  };

  const filteredTags = searchQuery.trim()
    ? internalTags.filter((t) => t.name.toLowerCase().includes(searchQuery.toLowerCase().trim()))
    : internalTags;

  return (
    <View style={styles.container}>
      {internalTags.length > 8 && (
        <ThemedInput
          value={searchQuery}
          onChangeText={setSearchQuery}
          placeholder="Filter available tags..."
          style={styles.search}
        />
      )}

      {filteredTags.length === 0 ? (
        <ThemedText style={[styles.emptyText, { color: textMuted }]}>
          {searchQuery ? "No matching tags found." : "No tags available."}
        </ThemedText>
      ) : (
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.horizScroll}>
          {filteredTags.map((t) => (
            <FilterChip
              key={t.id}
              label={t.name}
              icon="pricetag-outline"
              selected={t.id ? selectedTagIds.includes(t.id) : false}
              onPress={() => t.id && toggleTag(t.id)}
              compact
            />
          ))}
        </ScrollView>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingVertical: 4,
  },
  search: {
    marginBottom: 8,
    paddingVertical: 4,
    paddingHorizontal: 8,
    fontSize: 12,
  },
  horizScroll: {
    flexDirection: "row",
  },
  emptyText: {
    fontSize: 12,
    fontStyle: "italic",
    paddingVertical: 4,
  },
});
